import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft, ZoomIn, ZoomOut,
  MousePointer2, Loader2, AlertCircle, Info, X,
  Eye, Upload, Trash2, Copy, LayoutDashboard, Settings,
  Search, Check, FileText
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adUnitsApi, AdUnit, NETWORK_OPTIONS, NetworkKey, PublishTagsResponse, PublishedTag } from '../../lib/api/adUnits'
import { AdsFeedTable } from '../creative-studio/AdsFeedTable'

interface CreativeStudioTabProps {
  campaignId?: number
  adUnitId?: number
  onBack?: () => void
}

const ZOOM_LEVELS = [25, 50, 75, 100, 125, 150, 200]

export function CreativeStudioTab({
  campaignId,
  adUnitId,
  onBack,
}: CreativeStudioTabProps) {
  // Ad unit data
  const [adUnit, setAdUnit] = useState<AdUnit | null>(null)
  const [isLoadingAdUnit, setIsLoadingAdUnit] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Ads feed state
  const [activeAdId, setActiveAdId] = useState<number | null>(null)
  const [adsCount, setAdsCount] = useState(0)
  // Bumped every time an ad is created / updated / duplicated / deleted
  // so the preview iframes pick up the new HTML (the static preview
  // endpoint caches aggressively otherwise). It's appended as a `v=`
  // query param on the iframe `src`, which is enough to force a reload.
  const [adsRevision, setAdsRevision] = useState(0)
  const bumpAdsRevision = useCallback(() => setAdsRevision(r => r + 1), [])

  // Canvas state
  const [zoom, setZoom] = useState(100)
  const [showInfoPopup, setShowInfoPopup] = useState(false)
  const canvasRef = useRef<HTMLDivElement>(null)
  const infoRef = useRef<HTMLDivElement>(null)

  // Variant table resize — draggable right edge of the left column.
  // Persisted in localStorage so the chosen width survives reloads.
  const VARIANTS_MIN_WIDTH = 320
  const VARIANTS_MAX_WIDTH = 720
  const VARIANTS_DEFAULT_WIDTH = 480
  const VARIANTS_STORAGE_KEY = 'creative-studio:variantsWidth'
  const [variantsWidth, setVariantsWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return VARIANTS_DEFAULT_WIDTH
    const stored = window.localStorage.getItem(VARIANTS_STORAGE_KEY)
    const parsed = stored ? parseInt(stored, 10) : NaN
    if (!Number.isFinite(parsed)) return VARIANTS_DEFAULT_WIDTH
    return Math.min(VARIANTS_MAX_WIDTH, Math.max(VARIANTS_MIN_WIDTH, parsed))
  })
  const [isResizingVariants, setIsResizingVariants] = useState(false)
  const variantsPanelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try {
      window.localStorage.setItem(VARIANTS_STORAGE_KEY, String(variantsWidth))
    } catch { /* localStorage may be unavailable in private mode */ }
  }, [variantsWidth])

  const handleVariantsResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const panel = variantsPanelRef.current
    if (!panel) return
    const startLeft = panel.getBoundingClientRect().left
    setIsResizingVariants(true)

    const onMove = (ev: MouseEvent) => {
      const next = ev.clientX - startLeft
      const clamped = Math.min(
        VARIANTS_MAX_WIDTH,
        Math.max(VARIANTS_MIN_WIDTH, next),
      )
      setVariantsWidth(clamped)
    }
    const onUp = () => {
      setIsResizingVariants(false)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [])

  const handleVariantsResizeDoubleClick = useCallback(() => {
    setVariantsWidth(VARIANTS_DEFAULT_WIDTH)
  }, [])

  // Lock cursor + disable text selection globally during a drag so the
  // gesture stays consistent even if the pointer leaves the handle.
  useEffect(() => {
    if (!isResizingVariants) return
    const prevCursor = document.body.style.cursor
    const prevSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = prevCursor
      document.body.style.userSelect = prevSelect
    }
  }, [isResizingVariants])

  // Action states
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [publishingNetwork, setPublishingNetwork] = useState<NetworkKey | null>(null)
  const [networkSearchQuery, setNetworkSearchQuery] = useState('')
  const [tagsModalData, setTagsModalData] = useState<{ response: PublishTagsResponse } | null>(null)
  const [copiedTagIndex, setCopiedTagIndex] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Derive display sizes from static_preview_urls (authoritative WxH), fall back to adUnit.sizes
  const sizes = useMemo(() => {
    const staticUrls = adUnit?.preview_urls?.static_preview_urls
    if (staticUrls?.length) {
      return staticUrls.map(s => s.size)
    }
    return adUnit?.sizes || []
  }, [adUnit])
  // Zoom helpers
  const zoomIn = () => setZoom(z => Math.min(200, ZOOM_LEVELS.find(l => l > z) || z + 25))
  const zoomOut = () => setZoom(z => Math.max(25, [...ZOOM_LEVELS].reverse().find(l => l < z) || z - 25))
  // Reset is exposed via the "Reset zoom" text link in the bottom bar,
  // which inlines `setZoom(100)` directly — no separate handler needed.

  // Close info popup on outside click
  useEffect(() => {
    if (!showInfoPopup) return
    const handler = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowInfoPopup(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showInfoPopup])

  // Fetch ad unit on mount
  useEffect(() => {
    if (!campaignId || !adUnitId) return
    let cancelled = false
    setIsLoadingAdUnit(true)
    setLoadError(null)

    adUnitsApi.getAdUnit(campaignId, adUnitId)
      .then(unit => {
        if (cancelled) return
        setAdUnit(unit)
      })
      .catch(err => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load ad unit')
      })
      .finally(() => { if (!cancelled) setIsLoadingAdUnit(false) })

    return () => { cancelled = true }
  }, [campaignId, adUnitId])

  // Build all preview entries: each has size (WxH), w, h, and resolved url.
  // Preview URLs come from the backend as a *template* — we always tack on
  // `ad_id` (the currently selected variant) so the rendered HTML reflects
  // that variant's `variant_attribute` data, plus a `v=` cache buster that
  // bumps whenever an ad is mutated so users see their edits without a
  // hard refresh.
  const previewEntries = useMemo(() => {
    const previews = adUnit?.preview_urls
    if (!previews) return []

    const decorate = (rawUrl: string) => {
      const absolute = rawUrl.startsWith('//') ? `https:${rawUrl}` : rawUrl
      try {
        // Use a placeholder base so relative URLs still parse; only the
        // search params and the original `absolute` are used afterwards.
        const u = new URL(absolute, window.location.origin)
        if (activeAdId != null) u.searchParams.set('ad_id', String(activeAdId))
        if (adsRevision > 0) u.searchParams.set('v', String(adsRevision))
        return u.toString()
      } catch {
        // Fallback for exotic URLs the URL parser rejects — fall back to
        // dumb string concatenation so the preview still renders.
        if (activeAdId == null && adsRevision === 0) return absolute
        const sep = absolute.includes('?') ? '&' : '?'
        const parts: string[] = []
        if (activeAdId != null) parts.push(`ad_id=${activeAdId}`)
        if (adsRevision > 0) parts.push(`v=${adsRevision}`)
        return `${absolute}${sep}${parts.join('&')}`
      }
    }

    const staticUrls = previews.static_preview_urls
    if (staticUrls?.length) {
      return staticUrls.map(s => {
        const [w, h] = s.size.split('x').map(Number)
        return { size: s.size, w, h, url: decorate(s.url) }
      })
    }

    if (previews.base && sizes.length > 0) {
      return sizes.map(s => {
        const [w, h] = s.split('x').map(Number)
        return { size: s, w, h, url: decorate(previews.base) }
      })
    }

    return []
  }, [adUnit, sizes, activeAdId, adsRevision])

  const handleAdsCountChange = useCallback((count: number) => {
    setAdsCount(count)
  }, [])

  // ─── Action handlers ───
  const handlePreview = () => {
    const url = adUnit?.preview_urls?.base || adUnit?.preview_url || previewEntries[0]?.url
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      toast.error('No preview available')
    }
  }

  const handlePublishNetwork = async (network: NetworkKey) => {
    if (!campaignId || !adUnitId) return
    setPublishingNetwork(network)
    try {
      const response = await adUnitsApi.publishTags(campaignId, adUnitId, network)
      if (!response?.js_tags || !Array.isArray(response.js_tags)) {
        toast.error('No tags available for this network.')
        return
      }
      setTagsModalData({ response })
      setShowPublishModal(false)
    } catch {
      toast.error('Failed to generate tags.')
    } finally {
      setPublishingNetwork(null)
    }
  }

  const handleCopy = async () => {
    if (!campaignId || !adUnitId) return
    setIsCopying(true)
    try {
      await adUnitsApi.copyAdUnit(campaignId, adUnitId)
      toast.success('Ad unit copied')
    } catch {
      toast.error('Failed to copy ad unit')
    } finally {
      setIsCopying(false)
    }
  }

  const handleDelete = async () => {
    if (!campaignId || !adUnitId) return
    setIsDeleting(true)
    try {
      await adUnitsApi.deleteAdUnit(campaignId, adUnitId)
      toast.success('Ad unit deleted')
      onBack?.()
    } catch {
      toast.error('Failed to delete ad unit')
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const filteredNetworks = NETWORK_OPTIONS.filter(n =>
    n.name.toLowerCase().includes(networkSearchQuery.toLowerCase()) ||
    n.key.toLowerCase().includes(networkSearchQuery.toLowerCase())
  )

  const handleCopyTag = async (tag: PublishedTag, index: number) => {
    try {
      await navigator.clipboard.writeText(tag.content)
      setCopiedTagIndex(index)
      toast.success('Tag copied to clipboard')
      setTimeout(() => setCopiedTagIndex(null), 2000)
    } catch {
      toast.error('Failed to copy tag')
    }
  }

  const handleDashboard = () => {
    if (campaignId) {
      window.open(`/campaigns/${campaignId}#creativegeneration`, '_self')
    }
  }

  const adUnitName = adUnit?.name || adUnit?.content_type || (adUnitId ? `Ad Unit #${adUnitId}` : 'Creative Studio')
  const displayId = adUnit?.ad_unit_id ?? adUnit?.id ?? adUnitId

  const formatBool = (val: boolean | string | undefined | null) => {
    if (val === undefined || val === null) return null
    if (typeof val === 'string') return val
    return val ? 'On' : 'Off'
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: '2-digit', hour: '2-digit', minute: '2-digit'
      })
    } catch { return dateStr }
  }

  const infoFields: { label: string; value: string | null | undefined }[] = [
    { label: 'Id', value: displayId?.toString() },
    { label: 'Name', value: adUnit?.name },
    { label: 'Content Type', value: adUnit?.content_type },
    { label: 'Campaign', value: adUnit?.campaign_name || `#${adUnit?.campaign_id}` },
    { label: 'Sizes', value: adUnit?.sizes?.join(', ') },
    { label: 'Is flip', value: formatBool(adUnit?.is_flip) },
    { label: 'Auto Optimization', value: formatBool(adUnit?.auto_optimization) },
    { label: 'Auto rotate highlight', value: formatBool(adUnit?.auto_rotate_highlight) },
    { label: 'Auto rotate', value: formatBool(adUnit?.auto_rotate) },
    { label: 'Sequence', value: formatBool(adUnit?.sequence) },
    { label: 'Type', value: adUnit?.ad_type || adUnit?.content_type },
    { label: 'Status', value: adUnit?.status },
    { label: 'Created at', value: formatDate(adUnit?.created_at) },
    { label: 'Updated at', value: formatDate(adUnit?.updated_at) },
    { label: 'Has utm', value: formatBool(adUnit?.has_utm) },
    { label: 'Smart macros', value: formatBool(adUnit?.smart_macros) },
    { label: 'Smart tag', value: formatBool(adUnit?.smart_tag) },
    { label: 'Conversions tracking', value: formatBool(adUnit?.conversions_tracking) },
  ]

  // ─── Loading / error states ───
  if (isLoadingAdUnit) {
    return (
      <div className="h-full flex items-center justify-center bg-[#f0f0f0] dark:bg-[#1a1a2e]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading creative...</p>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="h-full flex items-center justify-center bg-[#f0f0f0] dark:bg-[#1a1a2e]">
        <div className="text-center max-w-xs">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">Failed to load</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{loadError}</p>
          {onBack && (
            <button onClick={onBack} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">
              Back to ad units
            </button>
          )}
        </div>
      </div>
    )
  }

  // ─── Main layout ───
  return (
    <div className="h-full flex flex-col bg-[#f0f0f0] dark:bg-[#1a1a2e]">
      {/* ─── Toolbar ─── */}
      <div className="h-11 flex items-center justify-between px-3 bg-white dark:bg-[#16162a] border-b border-gray-200/60 dark:border-gray-800/60 flex-shrink-0">
        {/* Left: Back + id | name... (i) */}
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button onClick={onBack} className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

          {/* ID | Name... (i) */}
          <div className="flex items-center gap-2 min-w-0 max-w-[300px]">
            {displayId && (
              <>
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 flex-shrink-0">{displayId}</span>
                <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
              </>
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400 truncate" title={adUnitName}>
              {adUnitName}
            </span>

            {/* Info icon + popup */}
            <div className="relative flex-shrink-0" ref={infoRef}>
              <button
                onClick={() => setShowInfoPopup(v => !v)}
                className={`p-1 rounded-full transition-colors ${
                  showInfoPopup
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title="Ad unit details"
              >
                <Info className="h-3.5 w-3.5" />
              </button>

              {showInfoPopup && adUnit && (
                // The popup used to centre under the info button with
                // `left-1/2 -translate-x-1/2`, but in the campaigns
                // layout the Creative Studio sits to the right of the
                // chat panel — and the chat panel's `backdrop-blur`
                // establishes its own stacking context. Centring the
                // popup pushed its left half into the chat column, and
                // because the two columns are siblings of the same
                // flex parent no amount of z-index can lift the popup
                // above its own column's neighbour.
                //
                // Anchoring `left-0` opens the popup to the right of
                // the info button, fully inside the Creative Studio
                // canvas, so the cross-column overlap goes away. We
                // also bump to `z-[60]` to clear other intra-studio
                // overlays (variant panel handle, etc.) without
                // affecting the global header (`z-[100]`).
                <div className="absolute top-full left-0 mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-white dark:bg-[#1e1e36] border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-[60] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-[#16162a] border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Ad Unit Details</span>
                    <button onClick={() => setShowInfoPopup(false)} className="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto px-4 py-3 space-y-2">
                    {infoFields.map(({ label, value }) => {
                      if (value === null || value === undefined) return null
                      return (
                        <div key={label} className="flex items-start gap-3">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium w-[140px] flex-shrink-0 text-right pt-px">{label}</span>
                          <span className="text-xs text-gray-800 dark:text-gray-200 break-words min-w-0">{value}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {sizes.length > 0 && (
            <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{sizes.length} size{sizes.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Zoom controls moved to the bottom status bar (sits next to
            the existing "Reset zoom" text link there). Header now only
            carries the ad-unit identity on the left and action buttons
            on the right. */}

        {/* Right: Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Icon actions group */}
          <div className="flex items-center gap-0.5 mr-1">
            <button onClick={handleCopy} disabled={isCopying} className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40" title="Copy ad unit">
              {isCopying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            <button onClick={() => setShowDeleteConfirm(true)} className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Delete ad unit">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={handleDashboard} className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Dashboard">
              <LayoutDashboard className="h-3.5 w-3.5" />
            </button>
            <button className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Settings">
              <Settings className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />

          {/* Labeled buttons */}
          <div className="flex items-center gap-1.5 ml-1">
            <button
              onClick={handlePreview}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <Eye className="h-3.5 w-3.5" />
              Preview
            </button>
            <button
              onClick={() => { setShowPublishModal(true); setNetworkSearchQuery('') }}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-sm"
            >
              <Upload className="h-3.5 w-3.5" />
              Publish
            </button>
          </div>
        </div>
      </div>

      {/* ─── Split: Variant table (left) + Preview (right) ─── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Variants feed table — width is user-resizable via the drag
            handle on its right edge. */}
        <div
          ref={variantsPanelRef}
          style={{ width: `${variantsWidth}px` }}
          className={`relative border-r border-gray-200/60 dark:border-gray-800/60 flex-shrink-0 overflow-hidden ${isResizingVariants ? '' : 'transition-[width] duration-200'}`}
        >
          <AdsFeedTable
            campaignId={campaignId!}
            adUnitId={adUnitId!}
            activeAdId={activeAdId}
            onSelectAd={setActiveAdId}
            onAdsCountChange={handleAdsCountChange}
            onAdsMutated={bumpAdsRevision}
            adUnitSizes={sizes}
          />

          {/* Resize handle — thin invisible strip on the right edge, highlights
              on hover/active. Double-click resets to default width. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize variants panel"
            onMouseDown={handleVariantsResizeStart}
            onDoubleClick={handleVariantsResizeDoubleClick}
            title="Drag to resize · double-click to reset"
            className="group absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-20 -mr-px"
          >
            <div
              className={`h-full w-px ml-auto transition-colors ${
                isResizingVariants
                  ? 'w-0.5 bg-primary-500'
                  : 'bg-transparent group-hover:w-0.5 group-hover:bg-primary-400/70'
              }`}
            />
          </div>
        </div>

        {/* Right: Preview canvas — all sizes grid */}
        <div ref={canvasRef} className="flex-1 overflow-auto relative min-w-0">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)', backgroundSize: '20px 20px', opacity: 0.15 }} />

          <div className="relative z-10 p-6">
            {previewEntries.length > 0 ? (
              <div className="flex flex-wrap gap-6 items-start">
                {previewEntries.map((entry) => {
                  const scale = zoom / 100
                  const outerW = entry.w * scale
                  const outerH = entry.h * scale
                  return (
                    <motion.div
                      key={entry.size}
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: 'spring', duration: 0.35 }}
                      className="flex flex-col items-center flex-shrink-0"
                    >
                      <div style={{ width: outerW, height: outerH, overflow: 'hidden' }}>
                        <iframe
                          src={entry.url}
                          title={`Preview ${entry.size}`}
                          frameBorder="0"
                          scrolling="no"
                          style={{
                            width: entry.w,
                            height: entry.h,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                            display: 'block',
                            border: 'none',
                          }}
                          sandbox="allow-scripts allow-same-origin"
                        />
                      </div>
                      <span className="mt-2 text-[10px] font-medium text-gray-400 dark:text-gray-500">
                        {entry.size}
                      </span>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="min-h-full flex items-center justify-center py-20">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-sm">
                  <div className="w-20 h-20 rounded-2xl bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center mx-auto mb-5">
                    <MousePointer2 className="h-9 w-9 text-gray-300 dark:text-gray-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">Your canvas is ready</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                    No preview available yet. Add variants or wait for creatives to generate.
                  </p>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Bottom status bar ─── */}
      <div className="h-7 flex items-center justify-between px-3 bg-white dark:bg-[#16162a] border-t border-gray-200/60 dark:border-gray-800/60 flex-shrink-0">
        <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500">
          {sizes.length > 0 && <span>{sizes.length} size{sizes.length !== 1 ? 's' : ''}: {sizes.join(', ')}</span>}
          <span>&middot;</span>
          <span>{adsCount} variant{adsCount !== 1 ? 's' : ''}</span>
          {adUnit?.content_type && (
            <>
              <span>&middot;</span>
              <span>{adUnit.content_type}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-gray-400 dark:text-gray-500">
          {/* Compact zoom controls — same pill that used to live in the
              top toolbar, now sitting next to the "Reset zoom" text. */}
          <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800/80 rounded-md px-1 py-0.5">
            <button onClick={zoomOut} className="p-0.5 rounded text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" title="Zoom out">
              <ZoomOut className="h-3 w-3" />
            </button>
            <span className="text-[10px] font-medium text-gray-600 dark:text-gray-300 w-9 text-center tabular-nums">{zoom}%</span>
            <button onClick={zoomIn} className="p-0.5 rounded text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" title="Zoom in">
              <ZoomIn className="h-3 w-3" />
            </button>
          </div>
          <button onClick={() => setZoom(100)} className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Reset zoom</button>
        </div>
      </div>

      {/* ─── Delete confirmation modal ─── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white dark:bg-[#1e1e36] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Delete Ad Unit</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
              Are you sure you want to delete <span className="font-medium">{adUnitName}</span>?
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={isDeleting} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                {isDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Network Selection Modal (same as CreativeGenerationTab) ─── */}
      <AnimatePresence>
        {showPublishModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Network</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Ad Unit: {adUnitName}
                  </p>
                </div>
                <button
                  onClick={() => setShowPublishModal(false)}
                  disabled={publishingNetwork !== null}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 border-b border-gray-100 dark:border-gray-700/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search networks..."
                    value={networkSearchQuery}
                    onChange={(e) => setNetworkSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-2">
                {filteredNetworks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No networks found matching &ldquo;{networkSearchQuery}&rdquo;
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {filteredNetworks.map((network) => {
                      const isThisPublishing = publishingNetwork === network.key
                      const isAnyPublishing = publishingNetwork !== null
                      return (
                        <button
                          key={network.key}
                          onClick={() => handlePublishNetwork(network.key)}
                          disabled={isAnyPublishing}
                          className={`p-3 text-left border rounded-lg transition-all relative ${
                            isThisPublishing
                              ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                              : isAnyPublishing
                              ? 'bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                              : 'bg-gray-50 dark:bg-gray-900 hover:bg-primary-50 dark:hover:bg-primary-900/20 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 group'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className={`text-sm font-medium ${
                                isThisPublishing
                                  ? 'text-primary-700 dark:text-primary-400'
                                  : 'text-gray-900 dark:text-white group-hover:text-primary-700 dark:group-hover:text-primary-400'
                              }`}>
                                {network.name}
                              </span>
                              <span className="block text-xs text-gray-400 mt-0.5">{network.key}</span>
                            </div>
                            {isThisPublishing && (
                              <Loader2 className="h-5 w-5 text-primary-600 dark:text-primary-400 animate-spin" />
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ─── Tags Display Modal (same as CreativeGenerationTab) ─── */}
      <AnimatePresence>
        {tagsModalData && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-2">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full h-[96vh] overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Ad Tags - {NETWORK_OPTIONS.find(n => n.key === tagsModalData.response.network)?.name || tagsModalData.response.network}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {tagsModalData.response.js_tags.length} tag{tagsModalData.response.js_tags.length !== 1 ? 's' : ''} generated for {adUnitName}
                  </p>
                </div>
                <button
                  onClick={() => setTagsModalData(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {tagsModalData.response.js_tags.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">No tags available for this network.</p>
                  </div>
                ) : (
                  tagsModalData.response.js_tags.map((tag, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          <span className="px-2.5 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 text-sm font-medium rounded">
                            {tag.size}
                          </span>
                          {tag.ad_unit_file_size && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {(tag.ad_unit_file_size / 1024).toFixed(1)} KB
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleCopyTag(tag, index)}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            copiedTagIndex === index
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                          }`}
                        >
                          {copiedTagIndex === index ? (
                            <>
                              <Check className="h-4 w-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Copy Tag
                            </>
                          )}
                        </button>
                      </div>
                      <div className="p-4">
                        <pre className="text-xs text-gray-300 bg-gray-900 dark:bg-black p-4 rounded-lg overflow-auto max-h-64 whitespace-pre-wrap break-all font-mono leading-relaxed">
                          <code>{tag.content}</code>
                        </pre>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-between items-center flex-shrink-0">
                <button
                  onClick={() => {
                    setTagsModalData(null)
                    setShowPublishModal(true)
                  }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  &larr; Choose Different Network
                </button>
                <button
                  onClick={() => setTagsModalData(null)}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
